import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ChefHat, Package, Home, Copy, Sparkles, Star, BellRing, ShieldCheck, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ChatWidget from "@/components/ChatWidget";
import { CurbsideCard } from "@/components/CurbsideCard";
import { rememberLoyaltyKey } from "@/lib/loyaltyKey";
import { T } from "@/context/TextEditContext";

/** Web Push helper — convert VAPID public key from url-safe base64 to
 * the Uint8Array format the browser's PushManager expects. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const STEPS = [
  { key: "Received", label: "Received", Icon: Package },
  { key: "Preparing", label: "Cooking", Icon: ChefHat },
  { key: "Ready", label: "Ready", Icon: CheckCircle2 },
];

function LoyaltyClaimCard({ order }) {
  const orderPhone = (order.phone || "").replace(/\D/g, "");
  const savedPhone = typeof window !== "undefined" ? localStorage.getItem("bk_loyalty_phone") : "";
  const [saved, setSaved] = useState(savedPhone === orderPhone);

  if (saved) {
    return (
      <div
        data-testid="loyalty-claimed-card"
        className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4 flex items-center gap-3"
      >
        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-emerald-900">
            +{order.points_earned} <T k="status.points_added" d="points added to your rewards!" />
          </div>
          <div className="text-[11px] text-emerald-700">
            Balance: <span className="font-bold">{order.points_balance} pts</span> ·{" "}
            <Link to="/rewards" className="underline font-bold">
              <T k="status.view_rewards" d="View rewards" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleClaim = async () => {
    try {
      const { data } = await api.post("/loyalty/signup", { phone: orderPhone, name: order.customer_name });
      rememberLoyaltyKey(data);
      localStorage.setItem("bk_loyalty_phone", orderPhone);
      if (order.customer_name) localStorage.setItem("bk_loyalty_name", order.customer_name);
      setSaved(true);
      toast.success("Points saved to your rewards!");
    } catch {
      toast.error("Couldn't save — try again from the Rewards page");
    }
  };

  return (
    <div
      data-testid="loyalty-claim-card"
      className="bg-brand-yellow/20 border-2 border-brand-yellow rounded-xl p-4 space-y-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-brand-red flex items-center justify-center shrink-0">
          <Star className="w-5 h-5 text-brand-yellow" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-2xl text-brand-red leading-none">
            +{order.points_earned} <T k="status.points_earned" d="points earned!" />
          </div>
          <div className="text-[11px] text-stone-700 mt-1">
            <T k="status.save_points_sub" d="Save them to your account" /> · Balance: {order.points_balance} pts
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={handleClaim}
        data-testid="loyalty-claim-btn"
        className="w-full bg-brand-red text-white py-2.5 rounded-full font-black uppercase tracking-widest text-xs hover:bg-red-800 active:scale-[0.98] transition-all"
      >
        <T k="status.save_btn" d="Save to Rewards" /> · {order.phone}
      </button>
    </div>
  );
}

export default function OrderStatusPage() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pushState, setPushState] = useState("idle"); // idle | enabling | on | denied | unsupported
  const [closingIn, setClosingIn] = useState(null);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [verifyPhone, setVerifyPhone] = useState("");
  const [verifying, setVerifying] = useState(false);
  const autoTriedRef = useRef(false);
  const navigate = useNavigate();

  const fetchOrder = useCallback(async () => {
    const token = localStorage.getItem(`bk_track_${orderNumber}`);
    try {
      const { data } = await api.get(`/orders/track/${orderNumber}`, {
        params: token ? { t: token } : {},
      });
      if (data.track_token) {
        localStorage.setItem(`bk_track_${orderNumber}`, data.track_token);
      }
      setOrder(data);
      setNeedsVerify(false);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        // Privacy guard — this device hasn't proven it owns the order.
        // Try once silently with the phone saved from checkout/loyalty.
        const saved = (localStorage.getItem("bk_loyalty_phone") || "").replace(/\D/g, "");
        if (saved.length >= 7 && !autoTriedRef.current) {
          autoTriedRef.current = true;
          try {
            const { data } = await api.get(`/orders/track/${orderNumber}`, {
              params: { phone: saved },
            });
            if (data.track_token) {
              localStorage.setItem(`bk_track_${orderNumber}`, data.track_token);
            }
            setOrder(data);
            return;
          } catch { /* fall through to manual verify */ }
        }
        setNeedsVerify(true);
      } else if (code === 404) {
        setNotFound(true);
      }
    }
  }, [orderNumber]);

  const submitVerify = async (e) => {
    e.preventDefault();
    const digits = verifyPhone.replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("Enter the phone number used at checkout");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.get(`/orders/track/${orderNumber}`, {
        params: { phone: digits },
      });
      if (data.track_token) {
        localStorage.setItem(`bk_track_${orderNumber}`, data.track_token);
      }
      setOrder(data);
      setNeedsVerify(false);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many attempts — please wait a few minutes and try again.");
      } else {
        toast.error("That phone number doesn't match this order.");
      }
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    const t = setInterval(fetchOrder, 8000);
    return () => clearInterval(t);
  }, [fetchOrder]);

  // Kick off the auto-close countdown as soon as the order transitions to
  // Completed. 8 seconds gives the customer time to see the "picked up"
  // confirmation before we redirect them out.
  useEffect(() => {
    if (order?.status === "Completed" && closingIn === null) {
      setClosingIn(8);
    }
  }, [order?.status, closingIn]);

  useEffect(() => {
    if (closingIn === null) return;
    if (closingIn <= 0) {
      navigate("/");
      return;
    }
    const t = setTimeout(() => setClosingIn((n) => (n ?? 0) - 1), 1000);
    return () => clearTimeout(t);
  }, [closingIn, navigate]);

  // Alert the customer via the browser tab title once the order is Ready
  // even if they've backgrounded the tab. Restores on unmount.
  useEffect(() => {
    const orig = document.title;
    if (order?.status === "Ready") {
      document.title = `🔔 Ready — ${order.order_number} · Blossom's Kitchen`;
    }
    return () => {
      document.title = orig;
    };
  }, [order?.status, order?.order_number]);

  // Detect existing push subscription state on load.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setPushState("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setPushState("denied");
      return;
    }
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        if (sub) setPushState("on");
      } catch { /* noop */ }
    })();
  }, []);

  const enablePush = async () => {
    if (!order) return;
    setPushState("enabling");
    try {
      // 1. Ask for permission (browser modal)
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setPushState(perm === "denied" ? "denied" : "idle");
        toast.error("Notifications denied. You'll still see updates on this page.");
        return;
      }
      // 2. Register service worker
      const reg = await navigator.serviceWorker.register("/push-sw.js");
      await navigator.serviceWorker.ready;
      // 3. Fetch VAPID key + subscribe
      const { data: { key } } = await api.get("/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(key);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      // 4. Send subscription to backend keyed by order id
      await api.post("/push/subscribe", {
        order_id: order.id,
        order_number: order.order_number,
        subscription: sub.toJSON(),
      });
      setPushState("on");
      toast.success("You'll get a push when your order's ready.");
    } catch (err) {
      console.error(err);
      setPushState("idle");
      toast.error("Couldn't enable notifications.");
    }
  };

  const copyNumber = () => {
    navigator.clipboard.writeText(orderNumber);
    toast.success("Order number copied");
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-stone-100 flex justify-center">
        <div className="w-full max-w-md lg:max-w-2xl min-h-screen bg-[#FDFBF7] phone-shell rustic-paper flex flex-col items-center justify-center px-6 text-center">
          <div className="font-hand text-4xl text-stone-500 mb-2">Not found</div>
          <p className="text-sm text-stone-500 mb-6">
            We couldn&apos;t find that order.
          </p>
          <Link
            to="/"
            className="bg-brand-red text-white px-6 py-2.5 rounded-full font-bold uppercase text-xs tracking-widest"
          >
            <T k="status.notfound_home" d="Home" />
          </Link>
        </div>
      </div>
    );
  }

  if (needsVerify && !order) {
    return (
      <div className="min-h-screen bg-stone-100 flex justify-center">
        <div className="w-full max-w-md lg:max-w-2xl min-h-screen bg-[#FDFBF7] phone-shell rustic-paper flex flex-col items-center justify-center px-6 text-center">
          <div
            data-testid="order-verify-screen"
            className="w-full max-w-sm space-y-4"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-brand-red flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-brand-yellow" />
            </div>
            <div className="font-hand text-4xl text-brand-red leading-tight">
              Let's make sure it's you
            </div>
            <p className="text-sm text-stone-600 leading-relaxed">
              For your privacy, order <span className="font-bold">#{orderNumber}</span>{" "}
              is only visible to the person who placed it. Enter the phone
              number used at checkout.
            </p>
            <form onSubmit={submitVerify} className="space-y-3">
              <div className="relative">
                <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  type="tel"
                  inputMode="tel"
                  autoFocus
                  value={verifyPhone}
                  onChange={(e) => setVerifyPhone(e.target.value)}
                  placeholder="(479) 555-1234"
                  data-testid="order-verify-phone-input"
                  className="w-full pl-11 pr-4 py-3 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-base font-mono tracking-wide"
                />
              </div>
              <button
                type="submit"
                disabled={verifying || verifyPhone.replace(/\D/g, "").length < 7}
                data-testid="order-verify-submit"
                className="w-full bg-brand-red text-white py-3 rounded-full font-black uppercase tracking-widest text-sm hover:bg-red-800 disabled:opacity-50 transition-all"
              >
                {verifying ? "Checking..." : "View My Order"}
              </button>
            </form>
            <Link
              to="/"
              data-testid="order-verify-home-link"
              className="inline-block text-xs text-stone-500 underline hover:text-stone-800"
            >
              Back to menu
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-stone-100 flex justify-center">
        <div className="w-full max-w-md lg:max-w-2xl min-h-screen bg-[#FDFBF7] phone-shell rustic-paper flex items-center justify-center">
          <div className="font-hand text-3xl text-stone-400">Loading...</div>
        </div>
      </div>
    );
  }

  const currentStepIdx = Math.max(
    0,
    STEPS.findIndex((s) => s.key === order.status)
  );
  const isCompleted = order.status === "Completed";
  const isCancelled = order.status === "Cancelled";
  const isReady = order.status === "Ready";

  return (
    <div className="min-h-screen bg-stone-100 flex justify-center">
      <div className="w-full max-w-md lg:max-w-2xl min-h-screen bg-[#FDFBF7] phone-shell rustic-paper">
        <div className="chalkboard-band px-4 py-4 border-b-4 border-brand-red text-center">
          <div className="text-[10px] uppercase tracking-widest text-brand-yellow/80">
            Order Number
          </div>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span
              className="font-display text-brand-yellow text-3xl"
              data-testid="order-number-display"
            >
              {order.order_number}
            </span>
            <button
              onClick={copyNumber}
              className="text-white/70 hover:text-white p-1"
              data-testid="copy-order-number-btn"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-6 pb-20">
          {isCompleted && closingIn !== null && (
            <div
              data-testid="picked-up-farewell"
              className="bg-emerald-600 text-white rounded-2xl p-5 text-center"
            >
              <div className="font-hand text-brand-yellow text-4xl leading-tight">
                Enjoy the food!
              </div>
              <div className="text-sm text-white/90 mt-2 leading-relaxed">
                See you next time. This screen will close in{" "}
                <span className="font-bold" data-testid="picked-up-countdown">
                  {closingIn}
                </span>{" "}
                second{closingIn === 1 ? "" : "s"}.
              </div>
              <button
                type="button"
                onClick={() => navigate("/")}
                data-testid="picked-up-close-now"
                className="mt-3 bg-white/20 hover:bg-white/30 text-white text-xs uppercase tracking-widest font-bold px-4 py-1.5 rounded-full"
              >
                <T k="status.close_now" d="Close now" />
              </button>
              <div className="mt-4 pt-3 border-t border-white/20">
                <Link
                  to={`/reviews?from=${order.order_number}`}
                  data-testid="picked-up-review-cta"
                  className="inline-flex items-center gap-1.5 text-brand-yellow font-black text-xs uppercase tracking-widest hover:underline"
                >
                  <Star className="w-3.5 h-3.5 fill-brand-yellow" />
                  Leave us a review
                </Link>
              </div>
            </div>
          )}

          {isReady && (
            <div
              data-testid="ready-alert"
              role="alert"
              aria-live="assertive"
              className="rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-5 flex items-center gap-4 animate-ready-glow"
            >
              <div className="w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                <BellRing className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-hand text-4xl text-emerald-800 leading-tight">
                  <T k="status.ready_title" d="Your order is ready!" />
                </div>
                <div className="text-sm text-emerald-900/80 mt-1">
                  <T k="status.ready_sub" d="Head to the truck and pick it up. Show" />{" "}
                  <span className="font-bold">#{order.order_number}</span>.
                </div>
              </div>
            </div>
          )}

          {!isCancelled && !isCompleted && !isReady && (
            <div
              data-testid="thank-you-banner"
              className="bg-brand-red text-white rounded-2xl p-4 text-center"
            >
              <div className="font-hand text-brand-yellow text-3xl leading-tight">
                Thanks for choosing Blossom&apos;s Kitchen!
              </div>
              <div className="text-[11px] uppercase tracking-widest text-white/85 font-bold mt-1">
                See you at the truck!
              </div>
            </div>
          )}

          {/* Web Push opt-in — customer can enable phone notifications */}
          {!isCompleted && !isCancelled && pushState !== "unsupported" && (
            <div
              data-testid="push-optin-card"
              className={`rounded-2xl border-2 p-4 flex items-center gap-3 ${
                pushState === "on"
                  ? "border-emerald-300 bg-emerald-50"
                  : "border-brand-yellow bg-brand-yellow/10"
              }`}
            >
              <BellRing className={`w-8 h-8 shrink-0 ${
                pushState === "on" ? "text-emerald-600" : "text-brand-red"
              }`} />
              <div className="flex-1 min-w-0">
                <div className="font-black text-stone-800 text-sm">
                  {pushState === "on"
                    ? "Notifications enabled"
                    : pushState === "denied"
                    ? "Notifications blocked"
                    : "Get pinged when it's ready"}
                </div>
                <div className="text-[11px] text-stone-600 mt-0.5">
                  {pushState === "on"
                    ? "We'll buzz your phone the moment the kitchen marks your order Ready."
                    : pushState === "denied"
                    ? "Turn on notifications in your browser settings to get pinged."
                    : "Let us send a push when your food is done — even if this tab is closed."}
                </div>
              </div>
              {pushState !== "on" && pushState !== "denied" && (
                <button
                  onClick={enablePush}
                  disabled={pushState === "enabling"}
                  data-testid="push-enable-btn"
                  className="bg-brand-red text-white px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-red-800 disabled:opacity-50 shrink-0"
                >
                  {pushState === "enabling" ? "…" : "Enable"}
                </button>
              )}
            </div>
          )}

          <div
            className={`rounded-2xl p-5 border-2 ${
              isCancelled
                ? "bg-stone-100 border-stone-300"
                : isCompleted
                ? "bg-emerald-50 border-emerald-300"
                : "bg-brand-yellow/10 border-brand-yellow"
            }`}
            data-testid="order-status-card"
          >
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              Status
            </div>
            <div className="font-display text-2xl mt-1 text-stone-900">
              {isCancelled
                ? "Cancelled"
                : isCompleted
                ? "Picked Up · Thank You!"
                : STEPS[currentStepIdx]?.label || order.status}
            </div>
            <div className="text-xs text-stone-600 mt-1">
              Pickup: <span className="font-bold">{order.pickup_time}</span>
            </div>
          </div>

          {!isCancelled && (
            <div className="grid grid-cols-3 gap-2" data-testid="progress-tracker">
              {STEPS.map((s, i) => {
                const active = i <= currentStepIdx;
                const current = i === currentStepIdx && !isCompleted;
                return (
                  <div key={s.key} className="text-center">
                    <div
                      className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                        active
                          ? "bg-brand-red text-white border-brand-red"
                          : "bg-white text-stone-300 border-stone-200"
                      } ${current ? "animate-gentle-pulse" : ""}`}
                    >
                      <s.Icon className="w-5 h-5" />
                    </div>
                    <div
                      className={`text-[10px] uppercase tracking-widest mt-1.5 font-bold ${
                        active ? "text-stone-800" : "text-stone-400"
                      }`}
                    >
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <section className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">
              <T k="status.your_order" d="Your Order" />
            </div>
            <ul className="space-y-1.5 text-sm">
              {order.items.map((i, idx) => (
                <li key={`${i.item_id}-${idx}`} className="flex justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-stone-700">
                      <span className="font-bold">{i.quantity}×</span> {i.name}
                    </span>
                    {i.modifications && (
                      <div className="text-[11px] text-stone-500 italic leading-tight">
                        {i.modifications}
                      </div>
                    )}
                  </div>
                  <span className="text-stone-800 font-bold whitespace-nowrap">
                    ${(i.price * i.quantity).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="border-t border-stone-200 mt-3 pt-3 space-y-1.5">
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-widest text-stone-500">
                  Subtotal
                </span>
                <span className="text-stone-800 font-bold">
                  ${order.subtotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-widest text-stone-500">
                  Sales Tax 9.5%
                </span>
                <span className="text-stone-800 font-bold">
                  ${(order.tax || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-baseline border-t border-stone-200 pt-2 mt-1">
                <span className="text-xs uppercase tracking-widest text-stone-500 font-bold">
                  {order.payment_method === "card" ? (
                    <T k="status.paid_by_card" d="Paid by Card" />
                  ) : (
                    <T k="common.total" d="Total" />
                  )}
                </span>
                <span className="font-display text-brand-red text-2xl">
                  ${(order.total || order.subtotal).toFixed(2)}
                </span>
              </div>
            </div>
            {order.reward && (
              <div className="mt-3 text-xs bg-emerald-50 border border-emerald-300 rounded p-2 text-emerald-800 font-bold">
                🎁 {order.reward}
              </div>
            )}
            {(order.points_earned > 0 || order.points_balance > 0) && (
              <div className="mt-3 text-[11px] bg-brand-yellow/20 border border-brand-yellow/50 rounded p-2 text-stone-800 flex items-center justify-between">
                <span>
                  {order.points_earned > 0 && (
                    <>
                      <span className="font-bold">+{order.points_earned} pts</span>{" "}
                      earned ·{" "}
                    </>
                  )}
                  Balance: <span className="font-bold">{order.points_balance}</span> pts
                </span>
              </div>
            )}
            {order.notes && (
              <div className="mt-3 text-xs text-stone-500 border-t border-stone-200 pt-3">
                <span className="font-bold"><T k="status.notes_label" d="Notes:" /></span> {order.notes}
              </div>
            )}
          </section>

          <CurbsideCard order={order} onUpdated={setOrder} />

          {order.payment_method === "card" && order.points_earned > 0 && (
            <LoyaltyClaimCard order={order} />
          )}

          <Link
            to="/"
            data-testid="order-home-link"
            className="w-full flex items-center justify-center gap-2 bg-stone-900 text-white py-3 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-stone-800"
          >
            <Home className="w-4 h-4" />
            <T k="status.back_to_menu" d="Back to Menu" />
          </Link>
        </div>
      </div>
      <ChatWidget orderNumber={order?.order_number || ""} customerPhone={order?.phone || ""} />
    </div>
  );
}
